<template lang="pug">
  .points-calculators
    .row
      .col-lg-5.mb-4
        article.points-calculator-card.h-100
          header.points-calculator-card__header
            .points-calculator-card__icon(aria-hidden="true")
              i.fa.fa-calculator
            div
              h3.points-calculator-card__title {{ $t('points_calculator.single_title') }}
              p.points-calculator-card__description {{ $t('points_calculator.single_description') }}

          section.points-calculator-card__body
            form(@submit.prevent)
              .form-row
                .col-sm-6.form-group
                  label(for="single-group-rank") {{ $t('points_calculator.rank') }}
                  input#single-group-rank.form-control(
                    v-model.number="singleRank"
                    type="number"
                    min="1"
                    :max="singleSize || maxParticipants"
                    :aria-invalid="!singleValid"
                  )
                .col-sm-6.form-group
                  label(for="single-group-size") {{ $t('points_calculator.group_size') }}
                  input#single-group-size.form-control(
                    v-model.number="singleSize"
                    type="number"
                    min="1"
                    :max="maxParticipants"
                    :aria-invalid="!singleValid"
                  )

            .points-calculator__details(v-if="singleValid")
              p.mb-1
                em(:title="$t('points_calculator.athlete_rank')") N
                |  = {{ singleRank }}
              p.mb-1
                em(:title="$t('points_calculator.group_size_calculated')") M
                |  = {{ singleCreditedSize }}
                small.text-muted &nbsp;({{ $t('points_calculator.credited_from', { size: singleSize }) }})
              p.small.text-muted.mb-0(v-if="singleOutsideCutoff") {{ $t('points_calculator.outside_cutoff') }}
            p.text-danger.small.mb-0(v-else role="alert") {{ $t('points_calculator.invalid_group') }}

          footer.points-calculator-card__footer
            .points-calculator__result-row
              span {{ $t('points_calculator.group_result') }}
              output.points-calculator__result(aria-live="polite") {{ formattedSinglePoints }}

      .col-lg-7.mb-4
        article.points-calculator-card.points-calculator-card--total.h-100
          header.points-calculator-card__header
            .points-calculator-card__icon(aria-hidden="true")
              i.fa.fa-plus
            div
              h3.points-calculator-card__title {{ $t('points_calculator.total_title') }}
              p.points-calculator-card__description {{ $t('points_calculator.total_description') }}

          section.points-calculator-card__body
            form(@submit.prevent)
              fieldset.points-calculator__group
                .points-calculator__group-header
                  legend.points-calculator__group-title {{ $t('points_calculator.category_group') }}
                  span.points-calculator__weight {{ $t('points_calculator.weight', { value: 33 }) }}
                .form-row
                  .col-sm-6.form-group
                    label(for="category-rank") {{ $t('points_calculator.category_rank') }}
                    input#category-rank.form-control(
                      v-model.number="categoryRank"
                      type="number"
                      min="1"
                      :max="categorySize || maxParticipants"
                      :aria-invalid="!categoryValid"
                    )
                  .col-sm-6.form-group
                    label(for="category-size") {{ $t('points_calculator.category_size') }}
                    input#category-size.form-control(
                      v-model.number="categorySize"
                      type="number"
                      min="1"
                      :max="maxParticipants"
                      :aria-invalid="!categoryValid"
                    )
                .points-calculator__formula(aria-live="polite")
                  span.points-calculator__formula-expression
                    em P₁
                    |  = {{ formattedCategoryPoints }} × 0.33
                  span.points-calculator__formula-result = {{ formattedCategoryContribution }}

              fieldset.points-calculator__group.mt-3
                .points-calculator__group-header
                  legend.points-calculator__group-title {{ $t('points_calculator.gender_group') }}
                  span.points-calculator__weight {{ $t('points_calculator.weight', { value: 67 }) }}
                .form-row
                  .col-sm-6.form-group
                    label(for="gender-rank") {{ $t('points_calculator.gender_rank') }}
                    input#gender-rank.form-control(
                      v-model.number="genderRank"
                      type="number"
                      min="1"
                      :max="genderSize || maxParticipants"
                      :aria-invalid="!genderValid"
                    )
                  .col-sm-6.form-group
                    label(for="gender-size") {{ $t('points_calculator.gender_size') }}
                    input#gender-size.form-control(
                      v-model.number="genderSize"
                      type="number"
                      min="1"
                      :max="maxParticipants"
                      :aria-invalid="!genderValid"
                    )
                .points-calculator__formula(aria-live="polite")
                  span.points-calculator__formula-expression
                    em P₂
                    |  = {{ formattedGenderPoints }} × 0.67
                  span.points-calculator__formula-result = {{ formattedGenderContribution }}

              p.text-danger.small.mt-3.mb-0(v-if="!totalValid" role="alert") {{ $t('points_calculator.invalid_group') }}

          footer.points-calculator-card__footer.points-calculator-card__footer--total
            .points-calculator__result-row
              span {{ $t('points_calculator.total_result') }}
              output.points-calculator__result(aria-live="polite") {{ formattedTotalPoints }}
</template>

<script>
import {
  combinedPoints,
  creditedGroupSize,
  formatContribution,
  formatPoints,
  groupPoints,
  isValidPointsGroup,
  weightedContribution
} from '../js/points_calculator';

export default {
  name: 'points-calculator',

  data() {
    return {
      maxParticipants: 100000,
      singleRank: 1,
      singleSize: 1000,
      categoryRank: 10,
      categorySize: 15,
      genderRank: 10,
      genderSize: 25
    };
  },

  computed: {
    singleValid() {
      return isValidPointsGroup(this.singleRank, this.singleSize);
    },
    categoryValid() {
      return isValidPointsGroup(this.categoryRank, this.categorySize);
    },
    genderValid() {
      return isValidPointsGroup(this.genderRank, this.genderSize);
    },
    totalValid() {
      return this.categoryValid && this.genderValid;
    },
    singleCreditedSize() {
      return creditedGroupSize(this.singleSize);
    },
    singleOutsideCutoff() {
      return this.singleValid && this.singleRank > this.singleCreditedSize;
    },
    singlePoints() {
      return groupPoints(this.singleRank, this.singleSize);
    },
    categoryPoints() {
      return groupPoints(this.categoryRank, this.categorySize);
    },
    genderPoints() {
      return groupPoints(this.genderRank, this.genderSize);
    },
    categoryContribution() {
      return weightedContribution(this.categoryPoints, 0.33);
    },
    genderContribution() {
      return weightedContribution(this.genderPoints, 0.67);
    },
    totalPoints() {
      return combinedPoints({
        categoryRank: this.categoryRank,
        categorySize: this.categorySize,
        genderRank: this.genderRank,
        genderSize: this.genderSize
      });
    },
    formattedSinglePoints() {
      return formatPoints(this.singlePoints);
    },
    formattedCategoryPoints() {
      return formatPoints(this.categoryPoints);
    },
    formattedGenderPoints() {
      return formatPoints(this.genderPoints);
    },
    formattedCategoryContribution() {
      return formatContribution(this.categoryContribution);
    },
    formattedGenderContribution() {
      return formatContribution(this.genderContribution);
    },
    formattedTotalPoints() {
      return formatPoints(this.totalPoints);
    }
  }
};
</script>

<style scoped>
.points-calculators {
  margin-top: 1.25rem;
}

.points-calculator-card {
  background: #fff;
  border: 1px solid #dbe3ec;
  border-radius: 12px;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.07);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.points-calculator-card--total {
  border-color: #a9c9e7;
  box-shadow: 0 12px 30px rgba(27, 94, 145, 0.11);
}

.points-calculator-card__header {
  align-items: flex-start;
  background: #fff;
  border-bottom: 1px solid #e6edf4;
  display: flex;
  gap: 0.85rem;
  padding: 1.25rem;
}

.points-calculator-card--total .points-calculator-card__header {
  background: #f5faff;
}

.points-calculator-card__icon {
  align-items: center;
  background: #e7f2fb;
  border-radius: 10px;
  color: #1b5e91;
  display: flex;
  flex: 0 0 2.5rem;
  height: 2.5rem;
  justify-content: center;
}

.points-calculator-card__title {
  color: #111827;
  font-size: 1.15rem;
  font-weight: 800;
  line-height: 1.25;
  margin: 0 0 0.3rem;
}

.points-calculator-card__description {
  color: #64748b;
  font-size: 0.9rem;
  line-height: 1.45;
  margin: 0;
}

.points-calculator-card__body {
  flex: 1 1 auto;
  padding: 1.25rem;
}

.points-calculator-card label {
  color: #334155;
  font-size: 0.9rem;
  font-weight: 700;
}

.points-calculator-card .form-control {
  border-color: #cbd5e1;
  border-radius: 8px;
  min-height: 46px;
}

.points-calculator-card .form-control:focus {
  border-color: #4c91c4;
  box-shadow: 0 0 0 0.2rem rgba(76, 145, 196, 0.18);
}

.points-calculator__group {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  margin: 0;
  min-width: 0;
  padding: 1rem;
}

.points-calculator__group-header {
  align-items: center;
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
  margin-bottom: 0.85rem;
}

.points-calculator__group-title {
  color: #1e293b;
  font-size: 1rem;
  font-weight: 800;
  line-height: 1.35;
  margin: 0;
  padding: 0;
}

.points-calculator__weight {
  background: #e7f2fb;
  border-radius: 999px;
  color: #1b5e91;
  flex: 0 0 auto;
  font-size: 0.75rem;
  font-weight: 800;
  padding: 0.3rem 0.55rem;
}

.points-calculator__formula {
  align-items: baseline;
  background: #fff;
  border: 1px solid #dbe7f0;
  border-left: 3px solid #4c91c4;
  border-radius: 7px;
  color: #1e293b;
  display: flex;
  flex-wrap: wrap;
  font-size: 1.05rem;
  gap: 0.3rem 0.45rem;
  line-height: 1.4;
  padding: 0.7rem 0.8rem;
}

.points-calculator__formula-result {
  color: #0f4f7d;
  font-weight: 800;
}

.points-calculator__result-row {
  align-items: baseline;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
}

.points-calculator-card__footer {
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  color: #334155;
  font-weight: 700;
  padding: 1rem 1.25rem;
}

.points-calculator-card__footer--total {
  background: #eaf5fd;
  border-top-color: #c9e0f1;
  color: #164c73;
}

.points-calculator__result {
  color: #0f4f7d;
  font-size: 1.65rem;
  font-weight: 900;
  letter-spacing: -0.02em;
}

@media (max-width: 575.98px) {
  .points-calculator-card__header,
  .points-calculator-card__body {
    padding: 1rem;
  }

  .points-calculator__group {
    padding: 0.85rem;
  }

  .points-calculator__group-header {
    align-items: flex-start;
  }

  .points-calculator__formula {
    font-size: 1rem;
  }
}
</style>
